/**
 * Rack query language — the text bar on the Rack Explorer.
 *
 * Deterministic, not generative: every hit is a row in the plant, and the
 * `intent` line says how the text was read so an operator can tell a miss
 * from a misparse. Grammar (case-insensitive, first match wins):
 *
 *   help | ?                         what you can type
 *   check | on site | todo           open "check on site" items
 *   path A to B | A -> B             shortest recorded path between racks/rooms
 *   links [of|from|in] X             every connection touching X
 *   fiber | copper | logical [in X]  links by medium
 *   verified | deduced | contested | unknown | vacant [in X]
 *   switches | ups | servers | … [in X]   units by kind
 *   10.0.3.133 | 10.0.3.             units by IP (prefix ok)
 *   network stanford | stanford      select a network: where it is, how it connects
 *   MDF | RR2 | IDF1 U3 | U12        focus a room, rack or unit
 *   anything else                    word search over labels, details, owners
 */
import {
  CONFIDENCE_LABEL,
  MEDIUM_LABEL,
  checkItems,
  networkFootprint,
  rackPath,
  rackTitle,
  ruLabel,
  unitTitle,
  type Confidence,
  type LinkMedium,
  type RackPlant,
  type RackUnitRow,
} from './rackModel';

export type QueryTarget = { type: 'room' | 'rack' | 'unit'; id: string };

export type QueryHit =
  | { type: 'room' | 'rack' | 'unit'; id: string; title: string; sub?: string }
  | { type: 'link'; id: string; title: string; sub?: string; unitId: string; otherRackId: string; otherUnitId: string | null };

export interface QueryResult {
  intent: string;
  hits: QueryHit[];
  focus?: QueryTarget;
  path?: { racks: string[]; linkIds: string[] };
  /** A network the query selected. */
  networkId?: string;
}

export const QUERY_EXAMPLES = [
  'network stanford',
  'path IDF3 to MDF',
  'links from MPOE',
  'IDF1 U3',
  '10.0.3.',
  'contested',
  'fiber',
  'check',
  'Stanford',
];

const CONFIDENCES: Confidence[] = ['verified', 'deduced', 'contested', 'unknown', 'vacant'];

const MEDIUM_WORDS: Record<string, LinkMedium[]> = {
  fiber: ['fiber', 'fiber_mm', 'fiber_sm'],
  fibre: ['fiber', 'fiber_mm', 'fiber_sm'],
  multimode: ['fiber_mm'],
  mmf: ['fiber_mm'],
  singlemode: ['fiber_sm'],
  smf: ['fiber_sm'],
  copper: ['copper'],
  logical: ['logical'],
};

const KIND_WORDS: Record<string, string> = {
  switch: 'switch', switches: 'switch',
  router: 'router', routers: 'router',
  firewall: 'firewall', firewalls: 'firewall',
  gateway: 'gateway', gateways: 'gateway',
  panel: 'patch_panel', panels: 'patch_panel', 'patch panels': 'patch_panel',
  server: 'server', servers: 'server',
  ups: 'ups', power: 'ups',
  pdu: 'pdu', pdus: 'pdu',
  shelf: 'shelf', shelves: 'shelf',
  phone: 'phone_system', phones: 'phone_system', telephony: 'phone_system', pbx: 'phone_system',
  kvm: 'kvm',
};

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9.]+/g, '');

/**
 * Resolve a phrase to a room, rack or unit. `contextRackId` lets a bare "U12"
 * mean U12 of the rack in view.
 */
export function resolveTarget(plant: RackPlant, phrase: string, contextRackId?: string | null): QueryTarget | null {
  const text = phrase.trim();
  if (!text) return null;

  const ru = /(?:^|\s)u\s*(\d{1,2})(?:\s*(rear|back))?\s*$/i.exec(text);
  if (ru) {
    const head = text.slice(0, ru.index).trim();
    const n = Number(ru[1]);
    const face = ru[2] ? 'rear' : undefined;
    const holder = head ? resolveTarget(plant, head, contextRackId) : contextRackId ? { type: 'rack' as const, id: contextRackId } : null;
    const rackIds =
      holder?.type === 'rack' ? [holder.id]
      : holder?.type === 'room' ? (plant.racksByRoom.get(holder.id) ?? []).map((r) => r.id)
      : [];
    for (const rackId of rackIds) {
      const unit = unitAt(plant, rackId, n, face);
      if (unit) return { type: 'unit', id: unit.id };
    }
    return holder;
  }

  const key = norm(text);
  for (const rack of plant.racks) {
    const room = plant.roomById.get(rack.room_id);
    const names = [rack.code, rack.name, room ? `${room.code}${rack.code}` : '', room ? `${room.code}${rack.name}` : ''];
    if (names.some((n) => n && norm(n) === key)) return { type: 'rack', id: rack.id };
  }
  for (const room of plant.rooms) {
    if (norm(room.code) === key || (room.name && norm(room.name) === key)) {
      const racks = plant.racksByRoom.get(room.id) ?? [];
      return racks.length === 1 ? { type: 'rack', id: racks[0].id } : { type: 'room', id: room.id };
    }
  }
  return null;
}

/** Match a network by code, name, first word of its name, or owner. */
export function resolveNetwork(plant: RackPlant, phrase: string): string | null {
  const key = norm(phrase);
  if (!key) return null;
  for (const n of plant.networks) {
    const names = [n.code, n.name, n.name.split(' ')[0], n.owner ?? ''];
    if (names.some((x) => x && norm(x) === key)) return n.id;
  }
  return null;
}

function unitAt(plant: RackPlant, rackId: string, ru: number, face?: 'rear'): RackUnitRow | undefined {
  const units = plant.unitsByRack.get(rackId) ?? [];
  const covers = (u: RackUnitRow) => ru >= u.ru_start && ru < u.ru_start + u.ru_height;
  return face
    ? units.find((u) => u.face === 'rear' && covers(u))
    : units.find((u) => u.face === 'front' && covers(u)) ?? units.find(covers);
}

function racksOf(plant: RackPlant, target: QueryTarget | null): Set<string> | null {
  if (!target) return null;
  if (target.type === 'rack') return new Set([target.id]);
  if (target.type === 'room') return new Set((plant.racksByRoom.get(target.id) ?? []).map((r) => r.id));
  const unit = plant.unitById.get(target.id);
  return unit ? new Set([unit.rack_id]) : null;
}

function targetTitle(plant: RackPlant, t: QueryTarget): string {
  if (t.type === 'room') {
    const room = plant.roomById.get(t.id);
    return room ? `${room.code}${room.name ? ` · ${room.name}` : ''}` : 'room';
  }
  return t.type === 'rack' ? rackTitle(plant, t.id) : unitTitle(plant, t.id);
}

function unitHit(plant: RackPlant, u: RackUnitRow, sub?: string): QueryHit {
  return {
    type: 'unit',
    id: u.id,
    title: `${u.label}`,
    sub: sub ?? `${rackTitle(plant, u.rack_id)} · ${ruLabel(u)} · ${CONFIDENCE_LABEL[u.confidence]}`,
  };
}

function linkHits(plant: RackPlant, unitIds: Iterable<string>, filter?: (medium: LinkMedium) => boolean): QueryHit[] {
  const hits: QueryHit[] = [];
  const seen = new Set<string>();
  for (const unitId of unitIds) {
    for (const end of plant.linksByUnit.get(unitId) ?? []) {
      if (seen.has(end.link.id) || (filter && !filter(end.link.medium))) continue;
      seen.add(end.link.id);
      const unit = plant.unitById.get(unitId)!;
      const far = end.otherUnitId ? plant.unitById.get(end.otherUnitId)?.label : null;
      hits.push({
        type: 'link',
        id: end.link.id,
        title: `${unit.label} → ${far ?? rackTitle(plant, end.otherRackId)}`,
        sub: `${MEDIUM_LABEL[end.link.medium]} · ${CONFIDENCE_LABEL[end.link.confidence]}${end.link.label ? ` · ${end.link.label}` : ''}`,
        unitId,
        otherRackId: end.otherRackId,
        otherUnitId: end.otherUnitId,
      });
    }
  }
  return hits;
}

function unitsIn(plant: RackPlant, scope: Set<string> | null): RackUnitRow[] {
  return plant.racks
    .filter((r) => !scope || scope.has(r.id))
    .flatMap((r) => plant.unitsByRack.get(r.id) ?? []);
}

/** Split "<head> in <scope>" and resolve the scope. */
function scoped(plant: RackPlant, rest: string, ctx?: string | null): { scope: Set<string> | null; scopeLabel: string } {
  const m = /^(?:in|at|of|from)\s+(.+)$/i.exec(rest.trim());
  if (!m) return { scope: null, scopeLabel: '' };
  const t = resolveTarget(plant, m[1], ctx);
  return t ? { scope: racksOf(plant, t), scopeLabel: ` in ${targetTitle(plant, t)}` } : { scope: new Set(), scopeLabel: ` in "${m[1]}" (not found)` };
}

export function runRackQuery(plant: RackPlant, input: string, contextRackId?: string | null): QueryResult {
  const q = input.trim().replace(/\s+/g, ' ');
  const lower = q.toLowerCase();

  if (!q || lower === 'help' || lower === '?') {
    return {
      intent: 'Try one of these',
      hits: QUERY_EXAMPLES.map((ex) => ({ type: 'room' as const, id: `example:${ex}`, title: ex })),
    };
  }

  if (/^(check|checks|on ?site|todo|to do)$/.test(lower)) {
    const items = checkItems(plant);
    return {
      intent: `Check on site · ${items.length} item${items.length === 1 ? '' : 's'}`,
      hits: items.map((u) => unitHit(plant, u, `${rackTitle(plant, u.rack_id)} · ${ruLabel(u)} · ${u.check_note}`)),
    };
  }

  const netMatch = /^(?:network|net|vlan|show network)\s+(.+)$/i.exec(q);
  const networkId = netMatch ? resolveNetwork(plant, netMatch[1]) : resolveNetwork(plant, q);
  if (netMatch && !networkId) return { intent: `Network: could not find "${netMatch[1]}"`, hits: [] };
  if (networkId) {
    const fp = networkFootprint(plant, networkId)!;
    const inRooms = fp.rooms.filter((r) => fp.roomIds.has(r.roomId));
    const codes = inRooms.map((r) => plant.roomById.get(r.roomId)!.code);
    const hits: QueryHit[] = inRooms.flatMap((r) => {
      const room = plant.roomById.get(r.roomId)!;
      const rows: QueryHit[] = r.units.map((u) => unitHit(plant, u, `${room.code} · ${rackTitle(plant, u.rack_id)} · ${ruLabel(u)}`));
      if (r.presenceOnly && r.units.length === 0) {
        rows.push({ type: 'room', id: room.id, title: `${room.code} · present, no device recorded`, sub: r.presenceOnly.note ?? undefined });
      }
      return rows;
    });
    return {
      intent: `${fp.network.name} · in ${codes.join(' + ') || 'no confirmed room'} · ${fp.unitIds.size} device${fp.unitIds.size === 1 ? '' : 's'} · ${fp.links.length} link${fp.links.length === 1 ? '' : 's'}`,
      hits,
      networkId,
    };
  }

  const pathMatch = /^(?:path|route|trace)?\s*(?:from\s+)?(.+?)\s*(?:->|→|\bto\b)\s*(.+)$/i.exec(q);
  if (pathMatch && (/^(path|route|trace|from)\b/i.test(q) || /->|→/.test(q))) {
    const a = resolveTarget(plant, pathMatch[1], contextRackId);
    const b = resolveTarget(plant, pathMatch[2], contextRackId);
    if (!a || !b) {
      return { intent: `Path: could not find ${!a ? `"${pathMatch[1]}"` : `"${pathMatch[2]}"`}`, hits: [] };
    }
    let best: ReturnType<typeof rackPath> = null;
    for (const from of racksOf(plant, a) ?? []) {
      for (const to of racksOf(plant, b) ?? []) {
        const p = rackPath(plant, from, to);
        if (p && (!best || p.racks.length < best.racks.length)) best = p;
      }
    }
    const label = `${targetTitle(plant, a)} → ${targetTitle(plant, b)}`;
    if (!best) return { intent: `Path ${label}: no recorded connection`, hits: [] };
    return {
      intent: `Path ${label} · ${best.links.length} hop${best.links.length === 1 ? '' : 's'}`,
      hits: best.racks.map((id, i) => ({
        type: 'rack' as const,
        id,
        title: rackTitle(plant, id),
        sub: i === 0 ? 'start' : `via ${best!.links[i - 1].label ?? MEDIUM_LABEL[best!.links[i - 1].medium]} · ${CONFIDENCE_LABEL[best!.links[i - 1].confidence]}`,
      })),
      focus: { type: 'rack', id: best.racks[best.racks.length - 1] },
      path: { racks: best.racks, linkIds: best.links.map((l) => l.id) },
    };
  }

  const linksMatch = /^(?:links?|connections?|connects?|where does)\s*(?:of|from|in|for|to)?\s*(.*?)(?:\s+connect)?$/i.exec(q);
  if (linksMatch) {
    const phrase = linksMatch[1];
    const t = phrase ? resolveTarget(plant, phrase, contextRackId) : contextRackId ? { type: 'rack' as const, id: contextRackId } : null;
    if (!t) return { intent: `Links: could not find "${phrase}"`, hits: [] };
    const unitIds = t.type === 'unit' ? [t.id] : unitsIn(plant, racksOf(plant, t)).map((u) => u.id);
    const hits = linkHits(plant, unitIds);
    return { intent: `Links touching ${targetTitle(plant, t)} · ${hits.length}`, hits, focus: t };
  }

  const [first, ...restWords] = lower.split(' ');
  const rest = restWords.join(' ');

  if (MEDIUM_WORDS[first]) {
    const media = MEDIUM_WORDS[first];
    const { scope, scopeLabel } = scoped(plant, rest, contextRackId);
    const hits = linkHits(plant, unitsIn(plant, scope).map((u) => u.id), (m) => media.includes(m));
    return { intent: `${cap(first)} links${scopeLabel} · ${hits.length}`, hits };
  }

  if ((CONFIDENCES as string[]).includes(first)) {
    const { scope, scopeLabel } = scoped(plant, rest, contextRackId);
    const hits = unitsIn(plant, scope).filter((u) => u.confidence === first).map((u) => unitHit(plant, u));
    return { intent: `${CONFIDENCE_LABEL[first as Confidence]} positions${scopeLabel} · ${hits.length}`, hits };
  }

  const kindKey = KIND_WORDS[lower] ? lower : KIND_WORDS[first] ? first : null;
  if (kindKey) {
    const kind = KIND_WORDS[kindKey];
    const { scope, scopeLabel } = scoped(plant, kindKey === lower ? '' : rest, contextRackId);
    const hits = unitsIn(plant, scope).filter((u) => u.kind === kind).map((u) => unitHit(plant, u));
    return { intent: `${cap(kindKey)}${scopeLabel} · ${hits.length}`, hits };
  }

  if (/^\d{1,3}(\.\d{0,3}){1,3}$/.test(q)) {
    const hits = plant.units
      .filter((u) => (u.ip_address ?? '').startsWith(q) || (u.detail ?? '').includes(q))
      .map((u) => unitHit(plant, u, `${u.ip_address ?? u.detail} · ${rackTitle(plant, u.rack_id)} · ${ruLabel(u)}`));
    return { intent: `Address ${q} · ${hits.length}`, hits, focus: hits.length === 1 ? { type: 'unit', id: hits[0].id } : undefined };
  }

  const target = resolveTarget(plant, q, contextRackId);
  if (target) {
    return { intent: `Go to ${targetTitle(plant, target)}`, hits: [{ type: target.type, id: target.id, title: targetTitle(plant, target) }], focus: target };
  }

  const words = lower.split(' ').filter(Boolean);
  const hits = plant.units
    .filter((u) => u.kind !== 'vacant')
    .filter((u) => {
      const room = plant.roomById.get(plant.rackById.get(u.rack_id)?.room_id ?? '');
      const hay = [u.label, u.detail, u.owner, u.check_note, u.ip_address, u.kind, room?.code, room?.name]
        .filter(Boolean).join(' ').toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    .map((u) => unitHit(plant, u));
  return { intent: `Search "${q}" · ${hits.length}`, hits };
}

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
